import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { MRT_ColumnFiltersState as MRTColumnFiltersState } from "material-react-table";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import { PermissionFilterParametersProps } from "permissions/_api/queries/filtering/permission-filter-schema";
import PermissionOrderParameters, {
  PermissionOrderField,
} from "permissions/_api/queries/ordering/permission-order-params";
import PermissionsQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import { getPermissionTemplatesQueryByInterface, updatePermissionTemplateInterfaceQuery } from "permissions/_stores/permission-templates-redux-slice";
import { getPermissionsQueryByInterface, updatePermissionInterfaceQuery } from "permissions/_stores/permissions-redux-slice";
import { CategoryLabel } from "permissions/_views/types/permission-types";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";


export type UsePermissionsQuerySyncState = {
  columnFilters: MRTColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<MRTColumnFiltersState>>;
  sorting: Array<{ id: string; desc: boolean }>;
  setSorting: React.Dispatch<React.SetStateAction<Array<{ id: string; desc: boolean }>>>;
  pagination: { pageIndex: number; pageSize: number };
  setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
  activeCategory: CategoryLabel | "";
  setActiveCategory: (value: CategoryLabel | "") => void;
  permissionsQuery: PermissionsQuery;
  permissionTemplatesQuery: PermissionTemplateQuery;
  clearToMainPermissionsView: () => void;
};

type SortingState = Array<{ id: string; desc: boolean }>;
type PaginationState = { pageIndex: number; pageSize: number };

function applyUpdater<T>(updater: React.SetStateAction<T>, prev: T): T {
  return typeof updater === "function" ? (updater as (prev: T) => T)(prev) : updater;
}

function parseInteger(value: string | null, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function dateValueToISOString(value: unknown): string | null {
  if (!value) return null;

  if (value instanceof AHDate) {
    return value.toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  if (typeof value === "object" && value !== null && "toISOString" in value) {
    try {
      const maybeIso = (value as { toISOString: () => string }).toISOString();
      return typeof maybeIso === "string" ? maybeIso : null;
    } catch {
      return null;
    }
  }

  return null;
}

function buildColumnFilters(search: string): MRTColumnFiltersState {
  const params = new URLSearchParams(search);
  const filters: MRTColumnFiltersState = [];

  if (params.has("granteeUserId")) {
    filters.push({ id: "grantee", value: params.get("granteeUserId")! });
  }
  if (params.has("grantorUserId")) {
    filters.push({ id: "grantor", value: params.get("grantorUserId")! });
  }
  if (params.has("resource")) {
    filters.push({ id: "resource", value: params.get("resource")! });
  }
  if (params.has("name")) {
    filters.push({ id: "name", value: params.get("name")! });
  }

  const minCreatedDateString = params.get("minCreatedDate");
  const maxCreatedDateString = params.get("maxCreatedDate");
  if (minCreatedDateString || maxCreatedDateString) {
    filters.push({
      id: "created",
      value: [AHDate.fromJSON(minCreatedDateString)?.value, AHDate.fromJSON(maxCreatedDateString)?.value],
    });
  }

  const minDisabledDateString = params.get("minDisabledDate");
  const maxDisabledDateString = params.get("maxDisabledDate");
  if (minDisabledDateString || maxDisabledDateString) {
    filters.push({
      id: "disabled",
      value: [AHDate.fromJSON(minDisabledDateString)?.value, AHDate.fromJSON(maxDisabledDateString)?.value],
    });
  }

  return filters;
}

function writeColumnFilters(params: URLSearchParams, filters: MRTColumnFiltersState) {
  params.delete("granteeUserId");
  params.delete("grantorUserId");
  params.delete("resource");
  params.delete("name");
  params.delete("minCreatedDate");
  params.delete("maxCreatedDate");
  params.delete("minDisabledDate");
  params.delete("maxDisabledDate");

  const getFilter = (id: string) => filters.find((f) => f.id === id);

  const grantee = getFilter("grantee");
  if (grantee?.value && typeof grantee.value === 'string') {
    params.set("granteeUserId", grantee.value);
  }

  const grantor = getFilter("grantor");
  if (grantor?.value && typeof grantor.value === 'string') {
    params.set("grantorUserId", grantor.value);
  }

  const resource = getFilter("resource");
  if (resource?.value && typeof resource.value === 'string') {
    params.set("resource", resource.value);
  }

  const name = getFilter("name");
  if (name?.value && typeof name.value === 'string') {
    params.set("name", name.value);
  }

  const created = getFilter("created");
  if (created) {
    const [minCreatedDate, maxCreatedDate] = (created.value as [unknown, unknown]) ?? [undefined, undefined];
    const minCreatedIso = dateValueToISOString(minCreatedDate);
    const maxCreatedIso = dateValueToISOString(maxCreatedDate);

    if (minCreatedIso) params.set("minCreatedDate", minCreatedIso);
    if (maxCreatedIso) params.set("maxCreatedDate", maxCreatedIso);
  }

  const disabled = getFilter("disabled");
  if (disabled) {
    const [minDisabledDate, maxDisabledDate] = (disabled.value as [unknown, unknown]) ?? [undefined, undefined];
    const minDisabledIso = dateValueToISOString(minDisabledDate);
    const maxDisabledIso = dateValueToISOString(maxDisabledDate);

    if (minDisabledIso) params.set("minDisabledDate", minDisabledIso);
    if (maxDisabledIso) params.set("maxDisabledDate", maxDisabledIso);
  }
}

function buildSorting(search: string): SortingState {
  const params = new URLSearchParams(search);
  const sortBy = params.get("sortBy");
  const sortDir = params.get("sortDir");

  if (!sortBy) {
    return [{ id: "grantee", desc: true }];
  }

  return [
    {
      id: sortBy,
      desc: sortDir !== "asc",
    },
  ];
}

function writeSorting(params: URLSearchParams, sorting: SortingState) {
  params.delete("sortBy");
  params.delete("sortDir");

  if (!sorting.length) return;

  params.set("sortBy", sorting[0].id);
  params.set("sortDir", sorting[0].desc ? "desc" : "asc");
}

function buildPagination(search: string): PaginationState {
  const params = new URLSearchParams(search);

  return {
    pageIndex: parseInteger(params.get("pageIndex"), 0),
    pageSize: parseInteger(params.get("pageSize"), 10),
  };
}

function writePagination(params: URLSearchParams, pagination: PaginationState) {
  params.set("pageIndex", String(pagination.pageIndex));
  params.set("pageSize", String(pagination.pageSize));
}

function buildFilterParameters(query: URLSearchParams): PermissionFilterParameters {
  const filters: PermissionFilterParametersProps = {
    showDisabled: true,
  };

  const granteeUserIds: Guid[] = [];

  const granteeUserIdString = query.get("granteeUserId");
  const granteeUserId = granteeUserIdString ? new Guid(granteeUserIdString) : undefined;
  if (granteeUserId && !granteeUserId.isEqualTo(Guid.EMPTY)) {
    granteeUserIds.push(granteeUserId);
  }

  const multiGranteeIds = query
    .getAll("granteeUserIds")
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => new Guid(c));

  if (multiGranteeIds.length) {
    granteeUserIds.push(...multiGranteeIds);
  }

  if (granteeUserIds.length) {
    filters.granteeUserIds = granteeUserIds;
  }

  const grantorUserIdString = query.get("grantorUserId");
  const grantorUserId = grantorUserIdString ? new Guid(grantorUserIdString, { allowEmpty: true }) : undefined;
  if (grantorUserId && !grantorUserId.isEqualTo(Guid.EMPTY)) {
    filters.grantorUserId = grantorUserId;
  }

  const nameString = query.get("name");
  if (nameString) {
    filters.name = nameString;
  }

  const resourceString = query.get("resource");
  if (resourceString) {
    filters.resource = resourceString;
  }

  const minCreatedDateString = query.get("minCreatedDate");
  const maxCreatedDateString = query.get("maxCreatedDate");

  const minCreatedDate = minCreatedDateString ? AHDate.fromJSON(minCreatedDateString) ?? undefined : undefined;
  const maxCreatedDate = maxCreatedDateString ? AHDate.fromJSON(maxCreatedDateString) ?? undefined : undefined;

  if (minCreatedDate) {
    filters.minCreatedDate = minCreatedDate;
  }
  if (maxCreatedDate) {
    filters.maxCreatedDate = maxCreatedDate;
  }

  const minDisabledDateString = query.get("minDisabledDate");
  const maxDisabledDateString = query.get("maxDisabledDate");

  const minDisabledDate = minDisabledDateString ? AHDate.fromJSON(minDisabledDateString) ?? undefined : undefined;
  const maxDisabledDate = maxDisabledDateString ? AHDate.fromJSON(maxDisabledDateString) ?? undefined : undefined;

  if (minDisabledDate) {
    filters.minDisabledDate = minDisabledDate;
  }
  if (maxDisabledDate) {
    filters.maxDisabledDate = maxDisabledDate;
  }

  const hasParentString = query.get("hasParent");
  if (hasParentString !== null) {
    filters.hasParent = hasParentString === "true";
  }

  const urlCategories = query
    .getAll("categories")
    .map((c) => c.trim())
    .filter(Boolean);
  if (urlCategories.length) {
    filters.categories = urlCategories;
  }

  const urlAccessType = query.get("accessType");
  if (urlAccessType) {
    filters.accessType = urlAccessType.trim();
  }

  return new PermissionFilterParameters(filters);
}

function buildOrderParameters(sorting: SortingState): PermissionOrderParameters {
  let orderBy: PermissionOrderField | undefined;

  if (sorting.length > 0 && sorting[0].id === "created") {
    orderBy = PermissionOrderField.CreatedDate;
  }
  if (sorting.length > 0 && sorting[0].id === "resource") {
    orderBy = PermissionOrderField.Resource;
  }
  if (sorting.length > 0 && sorting[0].id === "disabled") {
    orderBy = PermissionOrderField.DisabledDate;
  }

  return new PermissionOrderParameters({
    orderBy,
    orderDir: sorting.length > 0 && !sorting[0].desc ? OrderDirection.Ascending : OrderDirection.Descending,
  });
}

export default function usePermissionsQuerySync(): UsePermissionsQuerySyncState {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();

  const interfaceName = "permissionsTable";
  const templatesInterfaceName = "permissionTemplatesTable";

  const currentPermissionsQuery = useSelector(getPermissionsQueryByInterface(interfaceName));
  const currentPermissionTemplatesQuery = useSelector(
    getPermissionTemplatesQueryByInterface(templatesInterfaceName)
  );

  const columnFilters = useMemo(() => buildColumnFilters(location.search), [location.search]);
  const sorting = useMemo(() => buildSorting(location.search), [location.search]);
  const pagination = useMemo(() => buildPagination(location.search), [location.search]);
  const activeCategory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("category") as CategoryLabel) || "";
  }, [location.search]);

  const permissionsQuery = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const filterParameters = buildFilterParameters(query);
    const orderParameters = buildOrderParameters(sorting);
    const paginationParameters = PaginationParameters.create({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    });

    return new PermissionsQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
      permissionView: PermissionView.Grantee,
      previewSize: 3,
    });
  }, [location.search, sorting, pagination]);

  const permissionTemplatesQuery = useMemo(() => {
    return new PermissionTemplateQuery({
      filterParameters: permissionsQuery.filterParameters,
      orderParameters: permissionsQuery.orderParameters,
      paginationParameters: permissionsQuery.paginationParameters,
    });
  }, [permissionsQuery]);

  const updateSearch = useCallback(
    (mutate: (params: URLSearchParams) => void, replace = true) => {
      const current = new URLSearchParams(location.search);
      const next = new URLSearchParams(location.search);

      mutate(next);

      const currentSearch = current.toString();
      const nextSearch = next.toString();

      if (currentSearch === nextSearch) return;

      navigate({ search: nextSearch }, { replace });
    },
    [location.search, navigate]
  );

  const setColumnFilters = useCallback<React.Dispatch<React.SetStateAction<MRTColumnFiltersState>>>(
    (updater) => {
      const nextFilters = applyUpdater(updater, columnFilters);

      updateSearch((params) => {
        writeColumnFilters(params, nextFilters);
        params.set("pageIndex", "0");
      });
    },
    [columnFilters, updateSearch]
  );

  const setSorting = useCallback<React.Dispatch<React.SetStateAction<SortingState>>>(
    (updater) => {
      const nextSorting = applyUpdater(updater, sorting);

      updateSearch((params) => {
        writeSorting(params, nextSorting);
        params.set("pageIndex", "0");
      });
    },
    [sorting, updateSearch]
  );

  const setPagination = useCallback<React.Dispatch<React.SetStateAction<PaginationState>>>(
    (updater) => {
      const nextPagination = applyUpdater(updater, pagination);

      updateSearch((params) => {
        writePagination(params, nextPagination);
      });
    },
    [pagination, updateSearch]
  );

  const setActiveCategory = useCallback(
    (value: CategoryLabel | "") => {
      updateSearch((params) => {
        if (value) {params.set("category", value);}
        else {params.delete("category");}
      });
    },
    [updateSearch]
  );

  const clearToMainPermissionsView = useCallback(() => {
    updateSearch((params) => {
      params.delete("action");
      params.delete("granteeUserId");
      params.delete("hasParent");
      params.delete("category");
      params.delete("categories");
      params.delete("granteeUserIds");
      params.delete("accessType");
      params.delete("documentIds");
      params.delete("proposalIds");
      params.delete("workAgreementIds");
      params.delete("resource");
      params.delete("name");
      params.delete("grantorUserId");
      params.delete("minCreatedDate");
      params.delete("maxCreatedDate");
      params.delete("minDisabledDate");
      params.delete("maxDisabledDate");
      params.delete("sortBy");
      params.delete("sortDir");
      params.delete("pageIndex");
      params.delete("pageSize");
    });
  }, [updateSearch]);

  useEffect(() => {
    if (!currentPermissionsQuery?.isEqualTo(permissionsQuery)) {
      dispatch(
        updatePermissionInterfaceQuery({
          interfaceName,
          query: permissionsQuery,
        })
      );
    }

    if (
      !currentPermissionTemplatesQuery?.isEqualTo(permissionTemplatesQuery)
    ) {
      dispatch(
        updatePermissionTemplateInterfaceQuery({
          interfaceName: templatesInterfaceName,
          query: permissionTemplatesQuery,
        })
      );
    }
  }, [
    dispatch,
    interfaceName,
    templatesInterfaceName,
    permissionsQuery,
    permissionTemplatesQuery,
    currentPermissionsQuery,
    currentPermissionTemplatesQuery,
  ]);

  return {
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    pagination,
    setPagination,
    activeCategory,
    setActiveCategory,
    permissionsQuery,
    permissionTemplatesQuery,
    clearToMainPermissionsView,
  };
}
